/**
 * Converts a file to a base64 string.
 *
 * @param file - The file to convert.
 * @param includePrefix - If true, includes the 'data:type;base64,' prefix. Defaults to false.
 * @returns A Promise that resolves with the base64 string representation of the file (with or without prefix),
 *          or rejects if an error occurs during file reading.
 */
export function fileToBase64(
  file: File | Blob, // Typed the 'file' parameter
  includePrefix: boolean = false
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    // Explicitly type the Promise generic
    const reader = new FileReader()
    reader.readAsDataURL(file) // Read as data URL regardless

    reader.onload = () => {
      // reader.result is `string | ArrayBuffer | null`.
      // For readAsDataURL, it will be a string (or null if error, but onerror handles that).
      const resultString = reader.result as string // Assert as string

      if (includePrefix) {
        // If flag is true, return the full data URI
        resolve(resultString)
      } else {
        // Default behavior: return only the base64 part
        const commaIndex = resultString.indexOf(",")
        if (commaIndex !== -1) {
          resolve(resultString.substring(commaIndex + 1))
        } else {
          // Fallback: if no comma found (unlikely for valid data URLs from FileReader)
          resolve("")
        }
      }
    }

    reader.onerror = (error) => {
      // 'error' here is a ProgressEvent, reader.error has the DOMException
      reject(reader.error || error) // Reject with reader.error if available, else the event itself
    }
  })
}
